import { SETTINGS_PANEL_MAPPINGS } from '#~/analytics/pageview/constants.js';
import { useVirtualPageview } from '#~/analytics/pageview/index.js';
import type { SettingsPanelMappingKey } from '#~/analytics/pageview/types.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useIsAdminOrOwner } from '#~/room/hooks.js';
import { Dismiss } from '@studio/design-system/dialog';
import { Icon } from '@studio/design-system/icon';
import type { HtmlAttributes } from '@studio/design-system/props';
import * as Tabs from '@studio/design-system/tabs';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { toEnum } from '@tryiris-ai/js-types';
import { Sel, STagNew } from '#~/stag/index.js';
import type { ReactElement } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'cn';
import { Billing } from './billing/index.js';
import { Lang } from './constants.js';
import { Controllers } from './controllers/index.js';
import { Footer, HotKeys } from './hot-keys/index.js';
import { Organization } from './organization/index.js';
import { Password } from './password/index.js';
import { Profile } from './profile/index.js';
import { useProducer, useStore } from './store/hooks.js';
import { Panel, TopLevelPanel } from './store/index.js';
import { Studio } from './studio/index.js';

/** Account settings `<Scaffold>` component. */
export const Scaffold = ({ className, ...props }: HtmlAttributes): ReactElement => {
  const { enabled: isOrganizationEnabled } = useFeatureFlag(
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE
  );
  const isAdminOrOwner = useIsAdminOrOwner();

  const tabMap: Record<
    TopLevelPanel,
    T_Optional<Record<Panel, { component: unknown; hasFooter?: boolean }>>
  > = useMemo(() => {
    const baseMap: Partial<
      Record<TopLevelPanel, T_Optional<Record<Panel, { component: unknown; hasFooter?: boolean }>>>
    > = {
      [TopLevelPanel.Account]: {
        [Panel.Profile]: { component: Profile, hasFooter: true },
        [Panel.Password]: { component: Password },
        [Panel.HotKeys]: { component: HotKeys },
        [Panel.Controllers]: { component: Controllers }
      },
      [TopLevelPanel.Studio]: {
        [Panel.Studio]: { component: Studio, hasFooter: true },
        ...(isAdminOrOwner ? { [Panel.Billing]: { component: Billing } } : {})
      }
    };

    if (isOrganizationEnabled) {
      baseMap[TopLevelPanel.Organization] = {
        [Panel.Organization]: { component: Organization, hasFooter: true }
      };
    }

    return baseMap as Record<
      TopLevelPanel,
      T_Optional<Record<Panel, { component: unknown; hasFooter?: boolean }>>
    >;
  }, [isOrganizationEnabled, isAdminOrOwner]);

  const producer = useProducer();

  const [panel, topLevelPanel] = useStore((µ) => [µ.panel, µ.topLevelPanel]);
  const initialPlanPickerRoomId = useStore((µ) => µ[Panel.Billing].initialPlanPickerRoomId);
  // This local picker command is separate from the shared actual-display receipt.
  const consumePlanPickerRequest = useLatestFn((roomId: string) => {
    producer((draft) => {
      if (draft[Panel.Billing].initialPlanPickerRoomId === roomId) {
        delete draft[Panel.Billing].initialPlanPickerRoomId;
      }
    });
  });
  const [footer, setFooter] = useState<ReactElement | undefined>();

  const handleChangePanel = useLatestFn((nextValue: string) => {
    const nextPanel = toEnum(Panel, nextValue);

    if (nextPanel) {
      producer((draft) => {
        draft.panel = nextPanel;
      });
    }
  });

  const handleChangeTopLevelPanel = useLatestFn((nextValue: string) => {
    const nextPanel = toEnum(TopLevelPanel, nextValue);

    if (nextPanel) {
      producer((draft) => {
        draft.topLevelPanel = nextPanel;
        draft.panel = Object.keys(tabMap[nextPanel])[0] as Panel;
      });
    }
  });

  // Deal with overflow clipping of focus rings
  const passthru = {
    // Padding (32px) + padding (8px) adds up to 40px
    className: 'p-8'
  };

  useEffect(() => {
    let nextTopLevelPanel: T_Maybe<TopLevelPanel>;

    for (const t of Object.keys(tabMap)) {
      if (tabMap[t as TopLevelPanel][panel]) {
        nextTopLevelPanel = t as TopLevelPanel;

        break;
      }
    }

    if (nextTopLevelPanel && nextTopLevelPanel !== topLevelPanel) {
      producer((draft) => {
        draft.topLevelPanel = nextTopLevelPanel!;
      });
    }
  }, [panel, producer, tabMap, topLevelPanel]);

  // Panel → canonical virtual screen. useVirtualPageview owns the fire-once
  // rule and keys it on the canonical path, so a panel switch that resolves to
  // the same screen no longer emits a second $pageview. Typing the values as
  // SettingsPanelMappingKey means a screen name that no mapping declares now
  // fails to compile instead of silently emitting nothing.
  const pageviewMappingKeys: Record<
    TopLevelPanel,
    Partial<Record<Panel, SettingsPanelMappingKey>>
  > = {
    [TopLevelPanel.Account]: {
      [Panel.Profile]: 'account-profile',
      [Panel.Password]: 'account-password',
      [Panel.HotKeys]: 'account-hotkeys',
      [Panel.Controllers]: 'account-controllers'
    },
    [TopLevelPanel.Studio]: {
      [Panel.Studio]: 'studio-settings',
      [Panel.Billing]: 'studio-billing'
    },
    [TopLevelPanel.Organization]: {
      [Panel.Organization]: 'organization-settings'
    }
  };

  const pageviewMappingKey = pageviewMappingKeys[topLevelPanel]?.[panel];
  const pageviewMapping =
    pageviewMappingKey === undefined ? undefined : SETTINGS_PANEL_MAPPINGS[pageviewMappingKey];

  useVirtualPageview(pageviewMapping?.path ?? null, true, {
    ...(pageviewMapping === undefined ? {} : { category: pageviewMapping.category }),
    ...(pageviewMapping?.section === undefined ? {} : { section: pageviewMapping.section })
  });

  const icons = useMemo(
    () => ({
      [TopLevelPanel.Account]: 'profile',
      [TopLevelPanel.Studio]: 'home-2',
      [TopLevelPanel.Organization]: 'organization-2'
    }),
    []
  );

  return (
    <div
      className={twMerge('flex h-full w-sheet flex-col', className)}
      {...props}
      {...STagNew(Sel.AccountSettings, 'scaffold')}
    >
      <Tabs.Root
        className='grow px-32 pb-8 pt-[50px]'
        onChange={handleChangeTopLevelPanel}
        value={topLevelPanel}
      >
        <Tabs.List className='mx-8 mb-40 flex gap-28 px-0 text-md'>
          {Object.keys(tabMap).map((µ) => (
            <Tabs.Trigger key={µ} value={µ} {...STagNew(Sel.AccountSettings, 'top-tab', µ)}>
              <Icon
                className={twMerge(
                  'mr-4 !text-lg',
                  topLevelPanel === (µ as TopLevelPanel) && 'text-primary'
                )}
                icon={icons[µ as TopLevelPanel]}
              />
              {Lang.topLevelPanel[µ as TopLevelPanel]}
            </Tabs.Trigger>
          ))}

          <div className='flex-1' />

          <div className='flex flex-col items-start'>
            <Dismiss className='h-32 w-32' {...STagNew(Sel.AccountSettings, 'dismiss')} />
          </div>
        </Tabs.List>

        <Tabs.Root className='grow' onChange={handleChangePanel} value={panel}>
          <Tabs.List className='mx-8 mb-40 gap-28 px-0 text-md'>
            {tabMap[topLevelPanel] &&
              Object.keys(tabMap[topLevelPanel]!).map((µ) => (
                <Tabs.Trigger key={µ} value={µ} {...STagNew(Sel.AccountSettings, 'sub-tab', µ)}>
                  {Lang.panel[µ as Panel]}
                </Tabs.Trigger>
              ))}
          </Tabs.List>

          {!!tabMap[topLevelPanel][panel] && (
            <Tabs.Content value={panel}>
              {!!tabMap[topLevelPanel][panel] &&
                React.createElement(
                  tabMap[topLevelPanel][panel]!.component as React.FC<{ className: string }>,
                  {
                    ...passthru,
                    ...(panel === Panel.Billing
                      ? {
                          initialPlanPickerRoomId,
                          onInitialPlanPickerOpened: consumePlanPickerRequest
                        }
                      : {}),
                    ...(tabMap[topLevelPanel][panel]?.hasFooter ? { setFooter } : {})
                  }
                )}
            </Tabs.Content>
          )}
        </Tabs.Root>
      </Tabs.Root>

      {/* Only the hot keys panel has a footer. */}
      {panel === Panel.HotKeys && <Footer />}
      {!!tabMap[topLevelPanel][panel]?.hasFooter && footer}
    </div>
  );
};
